import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { get } from 'lodash';
import { useIntl } from 'react-intl';
import { isSameUser } from '../shared/identity';

const TOGGLE_BLOCK_CONVERSATION = gql`
  mutation toggleBlockConversation($id: ID!) {
    toggleBlockConversation(id: $id)
  }
`;

const BlockButton = ({ 
  currentUser, 
  conversationId, 
  conversation, 
  onConversationUpdate 
}) => {
  const intl = useIntl();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [toggleBlockConversation] = useMutation(TOGGLE_BLOCK_CONVERSATION, {
    // Refetch conversations list immediately after blocking
    refetchQueries: ['GetPersonalConversations'],
    awaitRefetchQueries: false, // Don't wait for refetch to complete - makes it feel instant
  });

  // Check if blocking is allowed for this user's company
  const blockingAllowed = get(currentUser, 'company.allow_blocking_in_messages', false);
  
  if (!blockingAllowed) {
    return null;
  }

  // Determine if the current user has blocked the conversation
  const currentUserIsParticipant1 = isSameUser(conversation?.participant1?.id, currentUser?.id);
  const isBlockedByCurrentUser = currentUserIsParticipant1 
    ? conversation?.blockedByParticipant1 
    : conversation?.blockedByParticipant2;

  const handleToggleBlock = async () => {
    // Immediately update local state for instant UI feedback
    if (onConversationUpdate) {
      const updatedConversation = {
        ...conversation,
        blockedByParticipant1: currentUserIsParticipant1 
          ? !isBlockedByCurrentUser 
          : conversation?.blockedByParticipant1,
        blockedByParticipant2: !currentUserIsParticipant1 
          ? !isBlockedByCurrentUser 
          : conversation?.blockedByParticipant2
      };
      onConversationUpdate(updatedConversation);
    }
    
    // Close menu immediately
    setIsMenuOpen(false);
    
    try {
      // Execute mutation in background
      await toggleBlockConversation({
        variables: { id: conversationId }
      });
    } catch (error) {
      console.error('🚫 BlockButton: Error toggling block status:', error);
      // Revert the optimistic update on error
      if (onConversationUpdate) {
        onConversationUpdate(conversation);
      }
    }
  };
  
  return (
    <div className="relative">
      {/* Menu Button */}
      <button
        onClick={() => {
          setIsMenuOpen(!isMenuOpen);
        }}
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          border: '2px solid var(--succession-error)',
          backgroundColor: 'var(--succession-error-bg)',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          outline: 'none'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = 'var(--succession-bg-primary)';
          e.target.style.borderColor = 'var(--succession-error)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'var(--succession-error-bg)';
          e.target.style.borderColor = 'var(--succession-error)';
        }}
        onFocus={(e) => {
          e.target.style.boxShadow = '0 0 0 2px var(--succession-error-bg)';
        }}
        onBlur={(e) => {
          e.target.style.boxShadow = 'none';
        }}
        title={intl.formatMessage({ id: 'succession.messaging.block_button.menu_title' })}
      >
        <svg 
          style={{ width: '20px', height: '20px', color: 'var(--succession-text-secondary)' }}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" 
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isMenuOpen && (
        <>
          {/* Overlay to close menu when clicking outside */}
          <div 
            style={{ 
              position: 'fixed',
              inset: 0,
              zIndex: 40
            }}
            onClick={() => setIsMenuOpen(false)}
          />
          
          {/* Menu */}
          <div style={{
            position: 'absolute',
            right: '8px',
            top: 0,
            zIndex: 50,
            width: '192px',
            backgroundColor: 'var(--succession-bg-primary)',
            borderRadius: '12px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            border: '1px solid var(--succession-border)',
            paddingTop: '8px',
            paddingBottom: '8px'
          }}>
            <button
              onClick={() => {
                handleToggleBlock();
              }}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: '14px',
                color: 'var(--succession-text-primary)',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--succession-bg-secondary)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              <svg 
                style={{ width: '16px', height: '16px' }}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                {isBlockedByCurrentUser ? (
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" 
                  />
                ) : (
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636" 
                  />
                )}
              </svg>
              {isBlockedByCurrentUser ? intl.formatMessage({ id: 'succession.messaging.block_button.unblock_chat' }) : intl.formatMessage({ id: 'succession.messaging.block_button.block_chat' })}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default BlockButton; 